import * as React from "react";
import {
  fetchAssessmentAudit,
  fetchEmployeeIdByAuthUserId,
  fetchPbgAnswers,
  fetchPbgQuestions,
  getCurrentUser,
  markAssessmentQuestionSkipped,
  unmarkAssessmentQuestionSkipped,
  upsertPbgAnswer,
} from "../../api/queries";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  Alert,
  Checkbox,
} from "@mui/material";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useI18n } from "../../i18n/I18nProvider";
import {
  SkippedQuestionBadge,
  SkippedQuestionNotice,
  skipQuestionButtonSx,
} from "../SkippedQuestionStatus";
import {
  AssessmentHeader,
  AssessmentSectionIntro,
  AssessmentSidebar,
  answerGridSx,
  assessmentContentSx,
  assessmentLayoutSx,
  assessmentOuterSx,
  assessmentOutlineButtonSx,
  assessmentPageSx,
  assessmentPanelSx,
  assessmentPrimaryButtonSx,
  getAnswerOptionSx,
  questionBodySx,
  questionCardSx,
  questionHeaderSx,
  questionLabelSx,
} from "../AssessmentTestLayout";
import type { AssessmentSectionProgress } from "../../types/assessment";
import type { EmployeeInfo, PbgQuestion, PbgSection } from "../../types/scores";


const TYPE_ORDER: PbgSection[] = ["G", "R", "V", "I", "ADMIN"];

const TYPE_SORT: Record<PbgSection, number> = {
  G: 1,
  R: 2,
  V: 3,
  I: 4,
  ADMIN: 5,
};

function getQuestionNumber(question: PbgQuestion) {
  const match = question.question_code?.match(/^Q(\d+)$/i);
  return match ? Number.parseInt(match[1], 10) : 9999;
}

function getQuestionGroup(question: PbgQuestion): PbgSection {
  if (["Q28", "Q29", "Q30"].includes(question.question_code ?? "")) return "ADMIN";
  return question.pbg_type ?? "ADMIN";
}

function getAnswerOptions(question: PbgQuestion, lang: "en" | "de") {
  const questionCode = question.question_code ?? "";

  if (questionCode === "Q28") {
    return [
      {
        value: "A",
        label: lang === "de" ? "Mehr als 2 Stunden" : "More than 2 hours",
      },
      {
        value: "B",
        label: lang === "de" ? "30-120 Minuten" : "30-120 minutes",
      },
      {
        value: "C",
        label: lang === "de" ? "Weniger als 30 Minuten" : "Less than 30 minutes",
      },
    ];
  }

  if (questionCode === "Q29") {
    return [
      {
        value: "calm",
        label: lang === "de" ? "Ruhe" : "Calm",
      },
      {
        value: "family",
        label: lang === "de" ? "Familie" : "Family",
      },
      {
        value: "plan",
        label: lang === "de" ? "Plan" : "Plan",
      },
      {
        value: "info",
        label: lang === "de" ? "Information" : "Information",
      },
      {
        value: "role",
        label: lang === "de" ? "Rolle" : "Role",
      },
    ];
  }

  if (questionCode === "Q30") {
    return [
      {
        value: "1",
        label: lang === "de" ? "Drei oder mehr" : "Three or more",
      },
      {
        value: "2",
        label: lang === "de" ? "Eine oder zwei" : "One or two",
      },
      {
        value: "3",
        label:
          lang === "de"
            ? "Keine — ich bin die einzige qualifizierte Person"
            : "None — I am the only qualified person",
      },
    ];
  }

  const enEntries = Object.entries(question.answer_options?.en ?? {});
  const deEntries = Object.entries(question.answer_options?.de ?? {});

  return enEntries.map(([enLabel, weight], index) => {
    const matchingDe = deEntries.find(([, deWeight]) => Number(deWeight) === Number(weight));
    const deLabel = matchingDe?.[0] ?? deEntries[index]?.[0] ?? enLabel;

    return {
      value: enLabel, // canonical value stored in DB for scored PBG questions
      label: lang === "de" ? deLabel : enLabel,
    };
  });
}

function parseMultiAnswer(value: string) {
  return value
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function stringifyMultiAnswer(values: string[]) {
  return values.join(",");
}

export default function PBGtest(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;

  const i18n: any = useI18n();
  const lang: "en" | "de" = i18n.lang ?? "en";
  const L = React.useCallback(
    (en: string, de: string) => (lang === "de" ? de : en),
    [lang]
  );

  const UI = React.useMemo(
    () => ({
      headerTitle: L("PBG Assessment", "PBG Assessment"),
      headerSubtitle: L(
        "Personal baseline guidance for your resilience profile.",
        "Persönliche Basisdiagnostik für dein Resilienzprofil."
      ),

      chipInfo: L("PBG", "PBG"),

      loading: L("Loading questions…", "Fragen werden geladen…"),
      saving: L("Saving…", "Speichern…"),
      noQuestions: L("No PBG questions found.", "Keine PBG-Fragen gefunden."),

      sidebarTitle: L("Your progress", "Dein Fortschritt"),
      sidebarSections: L("Sections", "Abschnitte"),
      sidebarOverall: (done: number, total: number) =>
        L(`${done} of ${total} questions done`, `${done} von ${total} Fragen erledigt`),
      sidebarRemaining: (remaining: number) =>
        remaining === 0
          ? L("All questions completed", "Alle Fragen abgeschlossen")
          : L(
              `${remaining} question${remaining === 1 ? "" : "s"} left`,
              `Noch ${remaining} ${remaining === 1 ? "Frage" : "Fragen"} offen`
            ),
      sidebarCount: (done: number, total: number) => `${done}/${total}`,
      sidebarSkipped: (skipped: number) =>
        L(`${skipped} skipped`, `${skipped} übersprungen`),
      sidebarEmpty: L("Nothing to show yet.", "Noch nichts anzuzeigen."),

      btnBack: L("Previous", "Zurück"),
      btnNext: L("Next", "Weiter"),
      btnContinueExtPrs: L("Continue with ExtPRS", "Weiter mit ExtPRS"),

      errNotAuth: L(
        "Not authenticated. Please log in again.",
        "Nicht angemeldet. Bitte erneut einloggen."
      ),
      errEmpNotFound: L(
        "Employee record not found for this account.",
        "Kein Mitarbeiterprofil für dieses Konto gefunden."
      ),
      errLoadQuestions: L(
        "Failed to load PBG questions.",
        "PBG-Fragen konnten nicht geladen werden."
      ),
      errLoadAnswers: L(
        "Failed to load previous answers.",
        "Vorherige Antworten konnten nicht geladen werden."
      ),
      errSave: L(
        "Failed to save your answer. Please try again.",
        "Antwort konnte nicht gespeichert werden. Bitte versuche es erneut."
      ),
      errSkip: L(
        "Failed to mark this question as skipped. Please try again.",
        "Die Frage konnte nicht als übersprungen markiert werden. Bitte versuche es erneut."
      ),
      btnSkip: L("Skip question", "Frage überspringen"),
      skippedLabel: L("Skipped", "Übersprungen"),
      skippedNotice: L(
        "You skipped this question. Default scoring will apply unless you answer it later.",
        "Du hast diese Frage übersprungen. Standardwert wird verwendet für die Bewertung, solange du sie später nicht beantwortest."
      ),

      sectionTitle: (type: PbgSection) => {
        if (type === "G") return L("Geopolitical Threat", "Geopolitische Bedrohung");
        if (type === "R") return L("Regional Exposure", "Regionale Nähe");
        if (type === "V") return L("Personal Vulnerability", "Persönliche Verletzlichkeit");
        if (type === "I") return L("Information Clarity", "Informationsklarheit");
        return L("Additional Questions", "Zusätzliche Fragen");
      },

      sectionSubtitle: (type: PbgSection) => {
        if (type === "G") {
          return L(
            "Questions about perceived threat, crisis awareness and general risk context.",
            "Fragen zur wahrgenommenen Bedrohung, Krisenwahrnehmung und allgemeinen Risikolage."
          );
        }
        if (type === "R") {
          return L(
            "Questions about regional, situational and environmental exposure.",
            "Fragen zur regionalen, situativen und umgebungsbezogenen Exposition."
          );
        }
        if (type === "V") {
          return L(
            "Questions about vulnerability, household needs and personal constraints.",
            "Fragen zu Verletzlichkeit, Haushaltsbedarf und persönlichen Einschränkungen."
          );
        }
        if (type === "I") {
          return L(
            "Questions about information access, clarity and ability to act on guidance.",
            "Fragen zu Informationszugang, Klarheit und Handlungsfähigkeit auf Basis von Empfehlungen."
          );
        }
        return L(
          "These questions are not scored in the PBG formula. They help tailor recommendations and identify organisational resilience risks.",
          "Diese Fragen fließen nicht in die PBG-Formel ein. Sie helfen, Empfehlungen anzupassen und organisatorische Resilienzrisiken zu erkennen."
        );
      },

      qLabel: (code: string | null, fallback: number) =>
        code ? code : L(`Question ${fallback}`, `Frage ${fallback}`),

      multiSelectHint: L("Choose up to 2 options.", "Wähle bis zu 2 Optionen aus."),
      maxTwoHint: L("You can select a maximum of 2 options.", "Du kannst maximal 2 Optionen auswählen."),
    }),
    [L]
  );

  const [employee, setEmployee] = React.useState<EmployeeInfo | null>(null);
  const [questions, setQuestions] = React.useState<PbgQuestion[]>([]);
  const [answers, setAnswers] = React.useState<Record<string, string>>({});
  const [skippedQuestionCodes, setSkippedQuestionCodes] = React.useState<string[]>([]);
  const [typeIndex, setTypeIndex] = React.useState(0);

  const [loading, setLoading] = React.useState(true);
  const [savingQuestionId, setSavingQuestionId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const currentType = TYPE_ORDER[typeIndex];

  const currentQuestions = React.useMemo(
    () => questions.filter((q) => getQuestionGroup(q) === currentType),
    [questions, currentType]
  );

  const isSkipped = React.useCallback(
    (question: PbgQuestion) =>
      Boolean(question.question_code && skippedQuestionCodes.includes(question.question_code)),
    [skippedQuestionCodes]
  );

  const isAnsweredOrSkipped = React.useCallback(
    (question: PbgQuestion) => Boolean(answers[question.id]) || isSkipped(question),
    [answers, isSkipped]
  );

  const answeredTotal = questions.filter((q) => isAnsweredOrSkipped(q)).length;

  const sidebarSections: AssessmentSectionProgress[] = React.useMemo(
    () =>
      TYPE_ORDER.map((type) => {
        const sectionQuestions = questions.filter((q) => getQuestionGroup(q) === type);

        return {
          key: type,
          label: UI.sectionTitle(type),
          answered: sectionQuestions.filter((q) => Boolean(answers[q.id])).length,
          skipped: sectionQuestions.filter((q) => !answers[q.id] && isSkipped(q)).length,
          total: sectionQuestions.length,
          active: type === currentType,
        };
      }).filter((section) => section.total > 0),
    [questions, answers, isSkipped, UI, currentType]
  );

  const isLastSection = typeIndex === TYPE_ORDER.length - 1;

  React.useEffect(() => {
    let mounted = true;

    async function loadAll() {
      setLoading(true);
      setError(null);

      try {
        const { data: userData, error: userErr } = await getCurrentUser();
        if (userErr || !userData?.user) throw new Error(UI.errNotAuth);

        const authUserId = userData.user.id;

        const { data: empRow, error: empErr } = await fetchEmployeeIdByAuthUserId(authUserId);

        if (empErr) throw empErr;
        if (!empRow) throw new Error(UI.errEmpNotFound);

        const { data: qRows, error: qErr } = await fetchPbgQuestions();

        if (qErr) throw new Error(UI.errLoadQuestions);

        const filteredQuestions = ((qRows ?? []) as PbgQuestion[]).filter((q) => {
          if (q.pbg_type === "G" || q.pbg_type === "R" || q.pbg_type === "V" || q.pbg_type === "I") {
            return true;
          }

          return ["Q28", "Q29", "Q30"].includes(q.question_code ?? "");
        });

        const sortedQuestions = filteredQuestions.sort((a, b) => {
          const aGroup = getQuestionGroup(a);
          const bGroup = getQuestionGroup(b);
          const typeDiff = TYPE_SORT[aGroup] - TYPE_SORT[bGroup];
          if (typeDiff !== 0) return typeDiff;

          const numDiff = getQuestionNumber(a) - getQuestionNumber(b);
          if (numDiff !== 0) return numDiff;

          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        });

        const { data: aRows, error: aErr } = await fetchPbgAnswers(empRow.id);

        if (aErr) throw new Error(UI.errLoadAnswers);

        const answerMap: Record<string, string> = {};
        for (const row of aRows ?? []) {
          answerMap[row.pbg_question_id] = row.given_answer;
        }

        const { data: auditRow } = await fetchAssessmentAudit(empRow.id);

        if (!mounted) return;

        setEmployee({ id: empRow.id });
        setSkippedQuestionCodes(((auditRow?.skipped_questions ?? []) as string[]).filter(Boolean));
        setQuestions(sortedQuestions);
        setAnswers(answerMap);
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message ?? UI.errLoadQuestions);
      } finally {
        if (!mounted) return;
        setLoading(false);
      }
    }

    loadAll();

    return () => {
      mounted = false;
    };
  }, [UI.errNotAuth, UI.errEmpNotFound, UI.errLoadQuestions, UI.errLoadAnswers]);

  async function saveAnswer(question: PbgQuestion, givenAnswer: string) {
    if (!employee) return;

    const questionCode = question.question_code;

    setSavingQuestionId(question.id);
    setError(null);

    const previousAnswer = answers[question.id];
    const previousSkipped = [...skippedQuestionCodes];

    setAnswers((old) => ({
      ...old,
      [question.id]: givenAnswer,
    }));

    if (questionCode) {
      setSkippedQuestionCodes((old) => old.filter((code) => code !== questionCode));
    }

    const { error: saveErr } = await upsertPbgAnswer(employee.id, question.id, givenAnswer);

    if (saveErr) {
      setAnswers((old) => {
        const next = { ...old };

        if (previousAnswer) {
          next[question.id] = previousAnswer;
        } else {
          delete next[question.id];
        }

        return next;
      });
      setSkippedQuestionCodes(previousSkipped);
      setSavingQuestionId(null);
      setError(UI.errSave);
      return;
    }

    if (questionCode && previousSkipped.includes(questionCode)) {
      const { error: unskipErr } = await unmarkAssessmentQuestionSkipped(employee.id, questionCode);

      if (unskipErr) {
        setSkippedQuestionCodes(previousSkipped);
        setError(UI.errSave);
      }
    }

    setSavingQuestionId(null);
  }

  async function skipQuestion(question: PbgQuestion) {
    const questionCode = question.question_code;
    if (!employee || !questionCode) return;

    setSavingQuestionId(question.id);
    setError(null);

    const previousAnswer = answers[question.id];
    const previousSkipped = [...skippedQuestionCodes];
    const nextSkipped = Array.from(new Set([...previousSkipped, questionCode]));

    setAnswers((old) => {
      const next = { ...old };
      delete next[question.id];
      return next;
    });

    setSkippedQuestionCodes(nextSkipped);

    const { error: skipErr } = await markAssessmentQuestionSkipped(employee.id, questionCode);

    if (skipErr) {
      setAnswers((old) => {
        const next = { ...old };

        if (previousAnswer) {
          next[question.id] = previousAnswer;
        }

        return next;
      });
      setSkippedQuestionCodes(previousSkipped);
      setError(UI.errSkip);
    }

    setSavingQuestionId(null);
  }

  function goBack() {
    if (typeIndex === 0) {
      handlePageChange("AvailabilityCheck");
      return;
    }

    setTypeIndex((v) => Math.max(v - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goNext() {
    if (isLastSection) {
      handlePageChange("ExtPRStest");
      return;
    }

    setTypeIndex((v) => Math.min(v + 1, TYPE_ORDER.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <Box sx={assessmentPageSx}>
      <Box sx={assessmentOuterSx}>
        <AssessmentHeader
          icon={<TuneOutlinedIcon />}
          title={UI.headerTitle}
          subtitle={UI.headerSubtitle}
        />

        <Box sx={assessmentLayoutSx}>
          <AssessmentSidebar
            title={UI.sidebarTitle}
            sections={sidebarSections}
            completed={answeredTotal}
            total={questions.length}
            overallLabel={UI.sidebarOverall}
            remainingLabel={UI.sidebarRemaining}
            sectionsHeading={UI.sidebarSections}
            countLabel={UI.sidebarCount}
            skippedLabel={UI.sidebarSkipped}
            emptyLabel={loading ? UI.loading : UI.sidebarEmpty}
          />

          <Paper elevation={0} sx={assessmentPanelSx}>
            <Box sx={assessmentContentSx}>
              <Stack spacing={2.25}>
                <AssessmentSectionIntro
                  eyebrow={`PBG · ${currentType === "ADMIN" ? "Q28-Q30" : currentType}`}
                  title={UI.sectionTitle(currentType)}
                  subtitle={UI.sectionSubtitle(currentType)}
                />

                {loading ? (
                  <Typography sx={{ fontWeight: 900 }}>{UI.loading}</Typography>
                ) : error ? (
                  <Alert severity="error">{error}</Alert>
                ) : questions.length === 0 ? (
                  <Alert severity="info">{UI.noQuestions}</Alert>
                ) : currentQuestions.length === 0 ? (
                  <Alert severity="info">{UI.noQuestions}</Alert>
                ) : (
                  <Stack spacing={2}>
                    {currentQuestions.map((question, localIndex) => (
                      <QuestionCard
                        key={question.id}
                        question={question}
                        questionNumber={localIndex + 1}
                        selectedAnswer={answers[question.id] ?? ""}
                        skipped={isSkipped(question)}
                        lang={lang}
                        saving={savingQuestionId === question.id}
                        qLabel={UI.qLabel(question.question_code, localIndex + 1)}
                        multiSelectHint={UI.multiSelectHint}
                        maxTwoHint={UI.maxTwoHint}
                        skippedLabel={UI.skippedLabel}
                        skippedNotice={UI.skippedNotice}
                        btnSkip={UI.btnSkip}
                        onChange={(value) => saveAnswer(question, value)}
                        onSkip={() => skipQuestion(question)}
                      />
                    ))}
                  </Stack>
                )}

                <Stack direction="row" justifyContent="space-between" sx={{ pt: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={goBack}
                    disabled={loading || Boolean(savingQuestionId)}
                    sx={assessmentOutlineButtonSx}
                  >
                    {UI.btnBack}
                  </Button>

                  <Button
                    variant="contained"
                    endIcon={<ArrowForwardIcon />}
                    disabled={loading || Boolean(savingQuestionId)}
                    onClick={goNext}
                    sx={assessmentPrimaryButtonSx}
                  >
                    {isLastSection ? UI.btnContinueExtPrs : UI.btnNext}
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}

function QuestionCard(props: {
  question: PbgQuestion;
  questionNumber: number;
  selectedAnswer: string;
  skipped: boolean;
  lang: "en" | "de";
  saving: boolean;
  qLabel: string;
  multiSelectHint: string;
  maxTwoHint: string;
  skippedLabel: string;
  skippedNotice: string;
  btnSkip: string;
  onChange: (value: string) => void;
  onSkip: () => void;
}) {
  const {
    question,
    selectedAnswer,
    skipped,
    lang,
    saving,
    qLabel,
    multiSelectHint,
    maxTwoHint,
    skippedLabel,
    skippedNotice,
    btnSkip,
    onChange,
    onSkip,
  } = props;

  const options = getAnswerOptions(question, lang);
  const isMultiSelect = question.question_code === "Q29";
  const selectedMultiValues = parseMultiAnswer(selectedAnswer);

  const handleMultiToggle = (value: string) => {
    if (saving) return;

    const current = parseMultiAnswer(selectedAnswer);
    const alreadySelected = current.includes(value);

    if (alreadySelected) {
      onChange(stringifyMultiAnswer(current.filter((item) => item !== value)));
      return;
    }

    if (current.length >= 2) return;

    onChange(stringifyMultiAnswer([...current, value]));
  };

  return (
    <Box sx={questionCardSx}>
      <Box sx={questionHeaderSx}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
          <Typography sx={questionLabelSx}>{qLabel}</Typography>

          {skipped && <SkippedQuestionBadge label={skippedLabel} />}
        </Stack>

        <Typography
          sx={{
            fontWeight: 900,
            color: "#111827",
            fontSize: { xs: 18, md: 20 },
            lineHeight: 1.35,
          }}
        >
          {lang === "de" ? question.question_de : question.question_en}
        </Typography>

        {isMultiSelect && (
          <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.75, fontWeight: 700 }}>
            {selectedMultiValues.length >= 2 ? maxTwoHint : multiSelectHint}
          </Typography>
        )}
      </Box>

      <Box sx={questionBodySx}>
        {isMultiSelect ? (
          <Box sx={answerGridSx}>
            {options.map((option) => {
              const selected = selectedMultiValues.includes(option.value);
              const disabled = saving || (!selected && selectedMultiValues.length >= 2);

              return (
                <FormControlLabel
                  key={option.value}
                  disabled={disabled}
                  control={
                    <Checkbox
                      checked={selected}
                      onChange={() => handleMultiToggle(option.value)}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontWeight: selected ? 900 : 800,
                        color: selected ? "#111827" : "#374151",
                        lineHeight: 1.35,
                      }}
                    >
                      {option.label}
                    </Typography>
                  }
                  sx={getAnswerOptionSx(selected, disabled)}
                />
              );
            })}
          </Box>
        ) : (
          <RadioGroup value={selectedAnswer} onChange={(e) => onChange(e.target.value)}>
            <Box sx={answerGridSx}>
              {options.map((option) => {
                const selected = selectedAnswer === option.value;

                return (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    disabled={saving}
                    control={<Radio />}
                    label={
                      <Typography
                        sx={{
                          fontWeight: selected ? 900 : 800,
                          color: selected ? "#111827" : "#374151",
                          lineHeight: 1.35,
                        }}
                      >
                        {option.label}
                      </Typography>
                    }
                    sx={getAnswerOptionSx(selected)}
                  />
                );
              })}
            </Box>
          </RadioGroup>
        )}

        {skipped && (
          <SkippedQuestionNotice label={skippedLabel} notice={skippedNotice} />
        )}

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
          <Button
            variant="text"
            onClick={onSkip}
            disabled={saving || skipped}
            sx={skipQuestionButtonSx}
          >
            {btnSkip}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
